import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MagicLogin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/api/auth/magic-link/verify`, { token });
      
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Welcome back! Logging you in...');
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired login link');
    } finally {
      setVerifying(false);
    }
  };

  const requestMagicLink = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/magic-link/request`, { email: email.trim() });
      setSent(true);
      toast.success('Check your email for the login link!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  // If verifying a token
  if (token) {
    if (verifying) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
                <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying Login Link</h2>
                <p className="text-slate-600">Please wait...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-900 mb-2">Login Link Invalid</h2>
                <p className="text-slate-600 mb-6">{error}</p>
                <Button onClick={() => navigate('/magic-login')} className="mr-2">
                  Request New Link
                </Button>
                <Button variant="outline" onClick={() => navigate('/login')}>
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return null;
  }

  // Email request form
  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Check Your Email</h2>
              <p className="text-slate-600 mb-2">
                We've sent a login link to:
              </p>
              <p className="font-medium text-slate-900 mb-6">{email}</p>
              <p className="text-sm text-slate-500 mb-6">
                Click the link in the email to log in. The link expires in 15 minutes.
              </p>
              <Button variant="outline" onClick={() => setSent(false)}>
                Use Different Email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-emerald-600" />
          </div>
          <CardTitle className="text-xl">Agent Portal Login</CardTitle>
          <CardDescription>
            Enter your email to receive a secure login link
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={requestMagicLink}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="mt-1"
                  data-testid="magic-link-email"
                  autoComplete="email"
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
                data-testid="magic-link-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Login Link
                  </>
                )}
              </Button>
            </div>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Prefer to use a password?{' '}
              <button 
                onClick={() => navigate('/login')} 
                className="text-emerald-600 hover:underline font-medium"
              >
                Sign in with password
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
